import { Injectable } from '@angular/core';
import { availablePrograms, eroiIds, TvdImpactCategory } from 'src/app/features/total-value-delivery/types/tvd-types';
import { CurrencyUomService, UomOption } from 'src/app/core/services/currency-uom.service';
import { FiltersService } from 'src/app/core/services/filters.service';

@Injectable({
  providedIn: 'root'
})
export class eroiService {

  private businessMetrics: eroiIds[] = [
    eroiIds.CASEGROWTH,
    eroiIds.TOTALCUSTOMERS,
    eroiIds.SALESGROWTH,
    eroiIds.CUSTOMERGROWTH,
    eroiIds.TOTALCASECOUNT,
    eroiIds.TOTALSALESDOLLARS,
    eroiIds.DELIVEREDINFULLONTIME,
    eroiIds.CASEFILLRATE,
    eroiIds.FULLPALLET,
    eroiIds.AVERAGEORDERSIZE
  ];

  constructor(
    private _filtersService: FiltersService,
    private _currencyUomService: CurrencyUomService) {
  }

  mapIdToTitle(id:string | undefined){
      let retval = "";
      switch (id){
      // Product TVD Metrics
      case eroiIds.WATER:
        retval = "Water";
        break;
      case eroiIds.ENERGY:
        retval = "Energy";
        break;
      case eroiIds.GREENHOUSEGASES:
        retval="Greenhouse Gases";
        break;
      case eroiIds.GREENHOUSEGASES_ENERGY:
        retval="CO2e from Energy Use";
        break;
      case eroiIds.GREENHOUSEGASES_TRANSPORT:
        retval="CO2e from Transportation";
        break;
      case eroiIds.GREENHOUSEGASES_PACKAGING:
        retval = "CO2e from Packaging Manufacture";
        break;
      case eroiIds.PLASTIC:
        retval="Plastic Waste";
        break;
      case eroiIds.PRODUCTIVITY:
        retval="Productivity";
        break;
      case eroiIds.ASSETS:
        retval="Assets";
        break;
      case eroiIds.CARDBOARD:
        retval="Cardboard Waste";
        break;
      case eroiIds.EMPLOYEESAFETY:
        retval="Employee Safety";
        break;
      case eroiIds.COSTAVOIDANCE:
        retval="Cost Avoidance";
        break;
      case eroiIds.PORTFOLIO:
        retval="Portfolio Reduction";
        break;
      case eroiIds.CASEGROWTH:
        retval="Case Growth"
        break;
      case eroiIds.TOTALCUSTOMERS:
        retval="Total Customers"
        break;
      case eroiIds.SALESGROWTH:
        retval="Sales Growth"
        break;
      case eroiIds.CUSTOMERGROWTH:
        retval="Customer +/-"
        break;
      case eroiIds.TOTALCASECOUNT:
        retval="TOTAL CASES"
        break;
      case eroiIds.TOTALSALESDOLLARS:
        retval="TOTAL SALES ($)"
        break;
      case eroiIds.DELIVEREDINFULLONTIME:
        retval="Delivered In Full On Time (%)";
        break;
      case eroiIds.CASEFILLRATE:
        retval="Case Fill Rate (%)";
        break;
      case eroiIds.FULLPALLET:
        retval="Full Pallet (%)";
        break;
      case eroiIds.AVERAGEORDERSIZE:
        retval="Avg. Order Size (Pounds)";
        break;
      case eroiIds.SOLID_WASTE:
        retval = "Solid Waste";
        break;
      // Services TVD Metrics
      case eroiIds.LABORPRODUCTIVITY: //Composite Tile title
        retval = "Labor Productivity";
        break;
      case eroiIds.NOCHARGEADDITIONALSERVICES:
        retval="No Charge Additional Services";
        break;
      case eroiIds.GENERALCONTRACTORCOSTAVOIDANCE:
        retval="General Contractor (GC) Cost Avoidance";
        break;
      case eroiIds.SETUPSERVICES:
        retval="Set-up Services";
        break;
      case eroiIds.PESTPREVENTIONFINDINGS:
        retval="Pest Prevention Findings";
        break;
      case eroiIds.ROOMNIGHTSSAVED:
        retval="Room Nights Saved";
        break;
      case eroiIds.FURNITUREPRESERVATIONASSETS:
        retval="Furniture Preservation (Assets)";
        break;
      case eroiIds.FURNITUREPRESERVATIONENERGY:
        retval="Furniture Preservation (Energy)";
        break;

      case eroiIds.CUSTOMTILE:
      default:
        retval="";
        //throw Error("Invalid Id in eroi-metric.service.ts : " + id);
      }

      return retval;
  }

  mapIdToEnterpriseTermsTitle(id:string | undefined){
    let retval = "";
    switch (id){
      case eroiIds.EMPLOYEESAFETY:
        retval = "Human Safety";
        break;
      case eroiIds.COSTAVOIDANCE:
      case eroiIds.ROOMNIGHTSSAVED:
        retval = "Reliability";
        break;
      case eroiIds.ASSETS:
      case eroiIds.FURNITUREPRESERVATIONASSETS:
        retval = "Asset Protection";
        break;
      case eroiIds.ENERGY:
      case eroiIds.FURNITUREPRESERVATIONENERGY:
        retval = "Electrical Energy";
        break;
      case eroiIds.PRODUCTIVITY:
        retval = "Labor Productivity";
        break;
      default:
        // For metrics that do not have a specific enterprise terms title, we will use the standard title
        retval = this.mapIdToTitle(id);
    }
    return retval;
  }

  mapIdToUnits(id: string | undefined, overrideMeasurementSystem?: UomOption){
    // Determine units based on user's UoM selection
    const isMetric = overrideMeasurementSystem ? overrideMeasurementSystem === 'Metric' : this._currencyUomService.getUom() === 'Metric';
    const weightUnits = isMetric ? 'kg' : 'lbs';
    const liquidUnits = isMetric ? 'liters' : 'gallons';
    const gasUnits = isMetric ? 'kg' : 'metric tons';
    const energyUnits = isMetric ? 'kWh' : 'therms';

    let retval = "";
    switch (id){
      // Product TVD metrics
      case eroiIds.WATER:
        retval = liquidUnits;
        break;
      case eroiIds.ENERGY:
        // Pest energy savings are always measured in kWh regardless of imperial/metric UoM setting
        retval = this._filtersService.selectedFilters.getValue()?.division === 'Pest' ? 'kWh' : `${energyUnits}`;
        break;
      case eroiIds.GREENHOUSEGASES:
      case eroiIds.GREENHOUSEGASES_ENERGY:
      case eroiIds.GREENHOUSEGASES_TRANSPORT:
      case eroiIds.GREENHOUSEGASES_PACKAGING:
        retval=`${gasUnits} of CO\u2082e`;
        break;
      case eroiIds.PLASTIC:
        retval= `${weightUnits} reduced`;
        break;
      case eroiIds.PRODUCTIVITY:
        retval="hours of labor reallocated per industry hourly average";
        break;
      case eroiIds.ASSETS:
        retval="high performance programs";
        break;
      case eroiIds.CARDBOARD:
        retval = `${weightUnits} reduced cardboard`;
        break;
      case eroiIds.EMPLOYEESAFETY:
        retval=`less ${weightUnits} lifted`;
        break;
      case eroiIds.COSTAVOIDANCE:
        retval="saved on maintenance, ongoing service, and repair parts";
        break;
      case eroiIds.PORTFOLIO:
        retval="reduced transport";
        break;
      // For Plastic + Cardboard composite component (Neptune UI)
      case eroiIds.SOLID_WASTE:
        retval=`${weightUnits}`;
        break;

      //For Labor Productivity composite component (Neptune UI)
      case eroiIds.LABORPRODUCTIVITY:
      retval=`Services/Findings`;
        break;

      // Service TVD metrics
      case eroiIds.NOCHARGEADDITIONALSERVICES:
        retval="Total Services";
        break;
      case eroiIds.GENERALCONTRACTORCOSTAVOIDANCE:
        retval="Exclusion Services";
        break;
      case eroiIds.SETUPSERVICES:
        retval="Services";
        break;
      case eroiIds.PESTPREVENTIONFINDINGS:
        retval="Sanitation and Structural Findings";
        break;
      case eroiIds.ROOMNIGHTSSAVED:
        retval="Nights";
        break;
      case eroiIds.FURNITUREPRESERVATIONENERGY:
        retval="kWh";
        break;
      case eroiIds.FURNITUREPRESERVATIONASSETS:
        retval="Mattress & Box Springs";
        break;
      default: //do not throw an error here because some metrics have no description
        retval="";
    }

    return retval;
}
  /**
   * @deprecated Remove once Neptune design has been implemented and old icons are not used
   */
  mapIdToGrayIconPath(id: string | undefined){
      let retval = "";
      switch (id) {
        case eroiIds.WATER :
          retval = "assets/tvd_icon_image_water_blue.png";
          break;
        case eroiIds.ENERGY :
          retval = "assets/tvd_icon_image_energy_blue.png";
          break;
        case eroiIds.GREENHOUSEGASES:
        case eroiIds.GREENHOUSEGASES_ENERGY:
        case eroiIds.GREENHOUSEGASES_TRANSPORT:
        case eroiIds.GREENHOUSEGASES_PACKAGING:
          retval = "assets/tvd_icon_image_greenhouse_gases.png";
          break;
        case eroiIds.PLASTIC :
          retval = "assets/tvd_icon_image_waste_blue.png";
          break;
        case eroiIds.CARDBOARD :
          retval = "assets/tvd_icon_image_waste_blue.png";
          break;
        case eroiIds.EMPLOYEESAFETY :
          retval = "assets/tvd_icon_image_safety_blue.png";
          break;
        case eroiIds.PRODUCTIVITY :
          retval = "assets/tvd_icon_image_productivity_blue.png";
          break;
        case eroiIds.ASSETS :
          retval = "assets/tvd_icon_image_assets.png";
          break;
        case eroiIds.COSTAVOIDANCE :
          retval = "assets/cost-avoidance.png";
          break;
        case eroiIds.PORTFOLIO :
          retval = "assets/tvd-gray-portfolio.png";
          break;
        case eroiIds.CUSTOMTILE :
          retval = "assets/tvd-gray-clipboard.png";
          break;
        case eroiIds.NOCHARGEADDITIONALSERVICES:
          retval = "assets/tvd_pest_additional_services.png";
          break;
        case eroiIds.GENERALCONTRACTORCOSTAVOIDANCE:
          retval = "assets/tvd_pest_cost_avoidance.png";
          break;
        case eroiIds.SETUPSERVICES:
          retval = "assets/tvd_pest_setup_services.png";
          break;
        case eroiIds.PESTPREVENTIONFINDINGS:
          retval = "assets/tvd_pest_prevention_findings.png";
          break;
        case eroiIds.ROOMNIGHTSSAVED:
          retval = "assets/tvd_pest_room_nights_saved.png";
          break;
        case eroiIds.FURNITUREPRESERVATIONASSETS:
          retval = "assets/tvd_pest_furniture_preservation_assets.png";
          break;
        case eroiIds.FURNITUREPRESERVATIONENERGY:
          retval = "assets/tvd_pest_furniture_preservation_energy.png";
          break;
        default:
          retval = "";
          break;
        }

      return retval;
  }

  /**
   * Maps the metric to their icons used in the new Neptune design system.
   */
  mapIdToMetricIconPath(id: string | undefined){
      let retval = "";
      switch (id) {
        case eroiIds.WATER :
          retval = "assets/total-value-delivery/metrics/tvd-water.svg";
          break;
        case eroiIds.ENERGY :
          retval = "assets/total-value-delivery/metrics/tvd-energy.svg";
          break;
        case eroiIds.GREENHOUSEGASES:
        case eroiIds.GREENHOUSEGASES_ENERGY:
        case eroiIds.GREENHOUSEGASES_TRANSPORT:
        case eroiIds.GREENHOUSEGASES_PACKAGING:
          retval = "assets/total-value-delivery/metrics/tvd-greenhouse-gases.svg";
          break;
        case eroiIds.PLASTIC :
        case eroiIds.SOLID_WASTE:
        case eroiIds.CARDBOARD :
          retval = "assets/total-value-delivery/metrics/tvd-waste.svg";
          break;
        case eroiIds.EMPLOYEESAFETY :
          retval = "assets/total-value-delivery/metrics/tvd-employee-safety.svg";
          break;
        case eroiIds.PRODUCTIVITY :
          retval = "assets/total-value-delivery/metrics/tvd-productivity.svg";
          break;
        case eroiIds.ASSETS :
        case eroiIds.FURNITUREPRESERVATIONASSETS:
        case eroiIds.SETUPSERVICES:
          retval = "assets/total-value-delivery/metrics/tvd-assets.svg";
          break;
        case eroiIds.COSTAVOIDANCE :
          retval = "assets/total-value-delivery/metrics/tvd-cost-avoidance.svg";
          break;
        case eroiIds.LABORPRODUCTIVITY:
          retval = "assets/total-value-delivery/impact-categories/labor-productivity-2x.svg";
          break;
        case eroiIds.NOCHARGEADDITIONALSERVICES:
          retval = "assets/total-value-delivery/metrics/tvd-no-charge-additional-services.svg";
          break;
        case eroiIds.GENERALCONTRACTORCOSTAVOIDANCE:
          retval = "assets/total-value-delivery/metrics/tvd-cost-avoidance.svg";
          break;
        case eroiIds.PESTPREVENTIONFINDINGS:
          retval = "assets/total-value-delivery/metrics/tvd-pest-prevention-findings.svg";
          break;
        case eroiIds.ROOMNIGHTSSAVED:
          retval = "assets/total-value-delivery/metrics/tvd-room-nights-saved.svg";
          break;
        case eroiIds.FURNITUREPRESERVATIONENERGY:
          retval = "assets/total-value-delivery/metrics/tvd-furniture-preservation-energy.svg";
          break;
        default:
          retval = "";
          break;
        }

      return retval;
  }

  /**
   * Returns the path to the icon used for summary pills on TVD landing page based on metric id.
   */
  mapIdToNeptuneSummaryPillIconPath(id: string | undefined){
    let retval = "";

    switch(id){
      case eroiIds.WATER:
        retval = "assets/total-value-delivery/summary-pills/tvd-water-summary.svg";
        break;
      case eroiIds.ENERGY:
        retval = "assets/total-value-delivery/summary-pills/tvd-energy-summary.svg";
        break;
      case eroiIds.SOLID_WASTE:
        retval = "assets/total-value-delivery/summary-pills/tvd-waste-summary.svg";
        break;
      default:
        retval = "";
        break;
    }

    return retval;
  }

  /**
   * Returns the path to the icons used by custom tiles based on impact category selected by the user.
   */
  mapImpactCategoryToIconPath(impactCategory: string | undefined){
    let retval = "";
    switch(impactCategory) {
      case TvdImpactCategory.AirEmissions:
      case TvdImpactCategory.GreenhouseGases:
        retval = "assets/total-value-delivery/impact-categories/greenhouse-gases.svg";
        break;
      case TvdImpactCategory.AssetProtection:
        retval = "assets/total-value-delivery/impact-categories/asset-protection.svg";
        break;
      case TvdImpactCategory.Brand:
        retval = "assets/total-value-delivery/impact-categories/brand.svg";
        break;
      case TvdImpactCategory.Capacity:
        retval = "assets/total-value-delivery/impact-categories/capacity.svg";
        break;
      case TvdImpactCategory.DelightedGuests:
        retval = "assets/total-value-delivery/impact-categories/delighted-guests.svg";
        break;
      case TvdImpactCategory.ElectricalEnergy:
        retval = "assets/total-value-delivery/impact-categories/electrical-energy.svg";
        break;
      case TvdImpactCategory.FoodSafety:
        retval = "assets/total-value-delivery/impact-categories/food-safety.svg";
        break;
      case TvdImpactCategory.HumanSafety:
        retval = "assets/total-value-delivery/impact-categories/human-safety.svg";
        break;
      case TvdImpactCategory.IndustryCompliance:
        retval = "assets/total-value-delivery/impact-categories/industry-compliance.svg";
        break;
      case TvdImpactCategory.LaborProductivity:
        retval = "assets/total-value-delivery/impact-categories/labor-productivity.svg";
        break;
      case TvdImpactCategory.LiquidWaste:
      case TvdImpactCategory.SolidWaste:
        retval = "assets/total-value-delivery/impact-categories/waste.svg";
        break;
      case TvdImpactCategory.LocationProductivity:
        retval = "assets/total-value-delivery/impact-categories/location-productivity.svg";
        break;
      case TvdImpactCategory.ProductOptimization:
        retval = "assets/total-value-delivery/impact-categories/product-optimization.svg";
        break;
      case TvdImpactCategory.Quality:
        retval = "assets/total-value-delivery/impact-categories/quality.svg";
        break;
      case TvdImpactCategory.RawMaterialOptimization:
        retval = "assets/total-value-delivery/impact-categories/raw-material-optimization.svg";
        break;
      case TvdImpactCategory.RegulatoryCompliance:
        retval = "assets/total-value-delivery/impact-categories/regulatory-compliance.svg";
        break;
      case TvdImpactCategory.Reliability:
        retval = "assets/total-value-delivery/impact-categories/reliability.svg";
        break;
      case TvdImpactCategory.ThermalEnergy:
        retval = "assets/total-value-delivery/impact-categories/thermal-energy.svg";
        break;
      case TvdImpactCategory.Water:
        retval = "assets/total-value-delivery/impact-categories/water.svg";
        break;
      case TvdImpactCategory.WaterSafety:
        retval = "assets/total-value-delivery/impact-categories/water-safety.svg";
        break;
      default:
        retval = "assets/total-value-delivery/impact-categories/asset-protection.svg";
        break;
    }
    return retval;
  }

  /**
   * Title mapping for the TVD summary pills on landing page
   */
  mapEroiIdToSummaryPillTitle(id: string | undefined) {
    let retval = "";
    switch(id){
      case eroiIds.WATER:
        retval = "Water";
        break;
      case eroiIds.ENERGY:
        retval = "Energy";
        break;
      case eroiIds.SOLID_WASTE:
        retval = "Waste";
        break;
      default:
        retval = "";
        break;
    }
    return retval;
  }


  getProgramKeys(program: string) : string[]{

    let keys : string[] = [];
    switch (program) {
      case availablePrograms.AQUANOMIC:
        keys = ["water", "energy", "productivity", "greenhouse-gases", "plastic-waste", "assets", "employee-safety"];
        break;
      case availablePrograms.SMARTPOWER:
        keys = ["water", "energy", "productivity", "greenhouse-gases", "plastic-waste", "assets", "cardboard-waste", "employee-safety"];
        break;
      case availablePrograms.ALL:
        keys = ["water", "energy", "productivity", "greenhouse-gases", "greenhouse-gases-energy", "greenhouse-gases-transport", "greenhouse-gases-packaging", "plastic-waste", "assets", "cardboard-waste", "employee-safety", "portfolio-reduction", "cost-avoidance", "furniture-preservation-assets", "furniture-preservation-energy", "room-nights-saved", "pest-prevention-findings", "set-up-services", "general-contractor-cost-avoidance", "no-charge-additional-services"];
        break;
      default:
        keys = [];
    }
    return keys;
  }


  IdIsInProgramKeys(id:string, program:string){
    let programKeys : string[] =[];
    if(program!=undefined){
      programKeys = this.getProgramKeys(program);
    }
    if(programKeys.includes(id)){
      return true;
    }
    return false;
  }

  isBusinessMetric(id : string) {
    return this.businessMetrics.includes(id as eroiIds);
  }
}
